import {
  ActionIcon,
  Divider,
  Group,
  Menu,
  Table,
  Text,
  Tooltip,
  Modal,
  TextInput,
  Select,
  Button,
  Stack,
  Space,
} from '@mantine/core';
import { openConfirmModal } from '@mantine/modals';
import { IconDots, IconPencil, IconTrashX, IconEye, IconRotate } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useForm } from '@mantine/form';
import { useDisclosure } from '@mantine/hooks';
import { useParams } from 'react-router-dom';
import { showNotification } from '@mantine/notifications';
import { useState } from 'react';

import { IntegrationRule } from '@mygpt/app/apis/services/lark';
import { updateWebConnectorV2, previewUrlPatterns, PreviewResponse } from '@mygpt/app/apis/services/website';
import { useWebConnectorStore } from '@/store/webConnector';
import { autoCompleteUrlScheme } from '@/utils/helper';
import { UrlPatternInput } from '@/components/Create_V2/components/Website/UrlPatternInput';
import { ScheduleSelector, ScheduleValue } from '@/components/Create_V2/components/Website/ScheduleSelector';
import { dedupeByCanonical } from '@/components/Create_V2/components/Website/csvUrlParser';

interface Props {
  connector: IntegrationRule;
  onView: (status?: string) => void;
}

/** 从 connector config 提取 V2 url_patterns（兼容 V1 数据） */
function getUrlPatterns(connector: IntegrationRule): string[] {
  const wc = connector.config?.web_connector;
  if (!wc) return [''];
  // V2: 后端 GET 已转换为 url_patterns
  if (wc.url_patterns && wc.url_patterns.length > 0) {
    return wc.url_patterns;
  }
  // V1 fallback: recursive 模式需要加 /** 表示全站
  if (wc.base_url) {
    const base = wc.base_url.replace(/\/+$/, '');
    return wc.crawl_mode === 'recursive' ? [`${base}/**`] : [base];
  }
  if (wc.url_list && wc.url_list.length > 0) return wc.url_list;
  return [''];
}

/** 从 connector config 提取 exclude_patterns */
function getExcludePatterns(connector: IntegrationRule): string[] {
  const patterns = connector.config?.web_connector?.exclude_patterns || [];
  return patterns.length > 0 ? patterns : [''];
}

/** 从 connector config 提取 schedule（兼容 V1） */
function getScheduleValue(connector: IntegrationRule): ScheduleValue {
  const config = connector.config;
  const schedule = config?.schedule;

  if (schedule) {
    return {
      enabled: config?.enable_sync ?? true,
      frequency: schedule.frequency || 'monthly',
      dayOfWeek: schedule.day_of_week ?? 1,
      dayOfMonth: schedule.day_of_month ?? 1,
      hour: schedule.hour ?? 9,
      minute: schedule.minute ?? 0,
    };
  }

  // V1 fallback: 从 sync_interval 推断
  const isMonthly = connector.sync_interval >= 30 * 24 * 3600;
  return {
    enabled: config?.enable_sync ?? connector.sync_interval > 0,
    frequency: isMonthly ? 'monthly' : 'weekly',
    dayOfWeek: 1,
    dayOfMonth: 1,
    hour: 9,
    minute: 0,
  };
}

/** 列表页显示的源地址（V1/V2 兼容） */
function getDisplayUrl(connector: IntegrationRule): string {
  const wc = connector.config?.web_connector;
  if (!wc) return '-';

  // V2: url_patterns
  if (wc.url_patterns && wc.url_patterns.length > 0) {
    const first = wc.url_patterns[0];
    const more = wc.url_patterns.length - 1;
    return more > 0 ? `${first} +${more}` : first;
  }

  // V1 fallback
  return wc.base_url || (wc.url_list || []).join(', ') || '-';
}

/** 列表页显示完整的 URL tooltip */
function getFullUrlTooltip(connector: IntegrationRule): string {
  const wc = connector.config?.web_connector;
  if (!wc) return '';
  if (wc.url_patterns && wc.url_patterns.length > 0) {
    return wc.url_patterns.join('\n');
  }
  return wc.base_url || (wc.url_list || []).join('\n') || '';
}

export const ConnectorListItem = ({ connector, onView }: Props) => {
  const { t } = useTranslation();
  const { datasetId } = useParams<{ datasetId: string }>();
  const [opened, handlers] = useDisclosure(false);

  const { deleteConnector, refreshConnector, fetchConnectors, connectors, sortBy, reverseSortDirection } =
    useWebConnectorStore();
  const [submitting, setSubmitting] = useState(false);

  // V2 编辑表单状态
  const [editUrlPatterns, setEditUrlPatterns] = useState<string[]>(() => getUrlPatterns(connector));
  const [editExcludePatterns, setEditExcludePatterns] = useState<string[]>(() => getExcludePatterns(connector));
  const [editSchedule, setEditSchedule] = useState<ScheduleValue>(() => getScheduleValue(connector));
  const [editMaxPages, setEditMaxPages] = useState<string>(String(connector.config?.web_connector?.max_pages || 400));
  const [editDiscoveryMode, setEditDiscoveryMode] = useState<string>(
    connector.config?.web_connector?.discovery_mode || 'auto'
  );
  const [editIsSpa, setEditIsSpa] = useState<string>(connector.config?.web_connector?.is_spa ? '1' : '0');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewResult, setPreviewResult] = useState<PreviewResponse | null>(null);

  const form = useForm<{ name: string }>({
    initialValues: {
      name: connector.name,
    },
    validate: {
      name: (value) => (value ? null : t('create_v2.integrations.task_management.validation.name_required')),
    },
  });

  // 打开编辑 Modal 时重置状态
  const handleOpenEdit = () => {
    form.setFieldValue('name', connector.name);
    setEditUrlPatterns(getUrlPatterns(connector));
    setEditExcludePatterns(getExcludePatterns(connector));
    setEditSchedule(getScheduleValue(connector));
    setEditMaxPages(String(connector.config?.web_connector?.max_pages || 400));
    setEditDiscoveryMode(connector.config?.web_connector?.discovery_mode || 'auto');
    setEditIsSpa(connector.config?.web_connector?.is_spa ? '1' : '0');
    setPreviewResult(null);
    handlers.open();
  };

  const handlePreview = async () => {
    if (!datasetId) return;
    const patterns = editUrlPatterns.filter((u) => u.trim()).map(autoCompleteUrlScheme);
    if (patterns.length === 0) return;
    setPreviewLoading(true);
    setPreviewResult(null);
    try {
      const excludes = editExcludePatterns.filter((u) => u.trim()).map(autoCompleteUrlScheme);
      const res = await previewUrlPatterns(datasetId, {
        url_patterns: patterns,
        exclude_patterns: excludes,
        max_pages: Number(editMaxPages) || 400,
        is_spa: Boolean(+editIsSpa),
        discovery_mode: editDiscoveryMode as 'auto' | 'sitemap' | 'crawler',
      });
      setPreviewResult(res.data);
    } catch (error: any) {
      showNotification({
        message: error?.response?.data?.detail || error.message || t('tip.update_error'),
        color: 'red',
      });
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSubmit = async (values: typeof form.values) => {
    if (!datasetId || submitting) return;

    try {
      setSubmitting(true);

      const { unique: urlPatterns, removed: dupRemoved } = dedupeByCanonical(
        editUrlPatterns.filter((u) => u.trim()).map(autoCompleteUrlScheme)
      );
      const excludePatterns = editExcludePatterns.filter((u) => u.trim()).map(autoCompleteUrlScheme);

      await updateWebConnectorV2(datasetId, connector.id, {
        name: values.name,
        config: {
          enable_sync: editSchedule.enabled,
          schedule: editSchedule.enabled
            ? {
                frequency: editSchedule.frequency,
                day_of_week: editSchedule.frequency === 'weekly' ? editSchedule.dayOfWeek : undefined,
                day_of_month: editSchedule.frequency === 'monthly' ? editSchedule.dayOfMonth : undefined,
                hour: editSchedule.hour,
                minute: editSchedule.minute,
                timezone: 'Asia/Tokyo',
              }
            : undefined,
          web_connector: {
            url_patterns: urlPatterns.length > 0 ? urlPatterns : getUrlPatterns(connector),
            exclude_patterns: excludePatterns,
            max_pages: Number(editMaxPages) || 400,
            is_spa: Boolean(+editIsSpa),
            discovery_mode: editDiscoveryMode as 'auto' | 'sitemap' | 'crawler',
          },
        },
      });

      showNotification({
        message: t('adminConsole.tip.update_success'),
        color: 'green',
      });

      if (dupRemoved > 0) {
        showNotification({
          message: t('create_v2.website.duplicates_removed', { count: dupRemoved }),
          color: 'orange',
        });
      }

      handlers.close();

      await fetchConnectors({
        datasetId,
        page: connectors.page,
        size: connectors.size,
        order_by: `${reverseSortDirection ? '-' : ''}${sortBy}`,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const deleteConnectorConfirm = () => {
    if (!datasetId) return;

    openConfirmModal({
      title: t('delete_btn'),
      centered: true,
      children: (
        <Text className="break-all">{t('dataset.web_connector.delete_confirm', { name: connector.name })}</Text>
      ),
      labels: { confirm: t('delete_btn'), cancel: t('alert_cancel') },
      onCancel: () => {},
      confirmProps: {
        color: 'red',
        onClick: async () => {
          try {
            await deleteConnector(datasetId, connector.id);
            showNotification({
              message: t('dataset.web_connector.delete_success'),
              color: 'green',
            });
          } catch (error) {
            showNotification({
              message: t('tip.delete_error'),
              color: 'red',
            });
          }
        },
      },
    });
  };

  const handleManualRefresh = () => {
    if (!datasetId) return;

    openConfirmModal({
      title: t('create_v2.integrations.task_management.manual_sync'),
      centered: true,
      children: (
        <Text>{t('create_v2.integrations.task_management.manual_sync_confirm', { name: connector.name })}</Text>
      ),
      labels: { confirm: t('alert_ok'), cancel: t('alert_cancel') },
      onCancel: () => {},
      confirmProps: {
        onClick: async () => {
          try {
            await refreshConnector(datasetId, connector.id);
            showNotification({
              message: t('create_v2.integrations.task_management.manual_sync_success'),
              color: 'green',
            });
          } catch (error: any) {
            const status = error?.response?.status;
            if (status === 429) {
              const detail = error?.response?.data?.detail || '';
              const match = detail.match(/(\d+)\s*seconds/);
              const seconds = match ? match[1] : '';
              showNotification({
                message: seconds
                  ? t('dataset.web_connector.sync_rate_limit_seconds', { seconds })
                  : t('dataset.web_connector.sync_rate_limit'),
                color: 'orange',
              });
            } else {
              showNotification({
                message: t('tip.update_error'),
                color: 'red',
              });
            }
          }
        },
      },
    });
  };

  // 展示同步间隔（V1/V2 兼容）
  const showRefreshInterval = (connector: IntegrationRule) => {
    if (connector.config?.enable_sync === false || connector.sync_interval <= 0) {
      return '-';
    }

    // V2: 有 schedule 对象
    const schedule = connector.config?.schedule;
    if (schedule) {
      const time = `${String(schedule.hour).padStart(2, '0')}:${String(schedule.minute ?? 0).padStart(2, '0')}`;
      if (schedule.frequency === 'daily') {
        return `${t('create_v2.website.schedule.daily')} ${time}`;
      }
      if (schedule.frequency === 'weekly') {
        const dayName = t(`create_v2.website.schedule.days.${schedule.day_of_week ?? 1}`);
        return `${t('create_v2.website.schedule.weekly')} ${dayName} ${time}`;
      }
      if (schedule.frequency === 'monthly') {
        const dayLabel = t('create_v2.website.schedule.day_format', { day: schedule.day_of_month ?? 1 });
        return `${t('create_v2.website.schedule.monthly')} ${dayLabel} ${time}`;
      }
      return time;
    }

    // V1 fallback: 与后端 get_schedule_config 逻辑对齐
    const interval = connector.config?.interval_seconds ?? connector.sync_interval ?? 604800;
    const time = '09:00';
    if (interval <= 86400) {
      return `${t('create_v2.website.schedule.daily')} ${time}`;
    }
    if (interval <= 604800) {
      const dayName = t('create_v2.website.schedule.days.1');
      return `${t('create_v2.website.schedule.weekly')} ${dayName} ${time}`;
    }
    const dayLabel = t('create_v2.website.schedule.day_format', { day: 1 });
    return `${t('create_v2.website.schedule.monthly')} ${dayLabel} ${time}`;
  };

  return (
    <>
      <Table.Tr>
        <Table.Td className="max-w-[200px]">
          <Tooltip label={connector.name} multiline className="max-w-[500px]  break-all">
            <Text size="sm" className="truncate">
              {connector.name}
            </Text>
          </Tooltip>
        </Table.Td>
        <Table.Td className="max-w-[120px]">
          <Tooltip label={getFullUrlTooltip(connector)} multiline className="max-w-[500px]  break-all">
            <Text size="sm" lineClamp={1}>
              {getDisplayUrl(connector)}
            </Text>
          </Tooltip>
        </Table.Td>
        <Table.Td>
          <Text size="sm">{connector.file_count}</Text>
        </Table.Td>
        <Table.Td>
          <Text size="sm">
            {t(`dataset.web_connector.status_${connector.file_list_sync_status}`, connector.file_list_sync_status)}
          </Text>
        </Table.Td>
        <Table.Td>
          <Stack gap={4}>
            <Group
              gap={4}
              className="cursor-pointer"
              onClick={() => {
                onView('complete');
              }}
            >
              <Text size="sm" c="gray.6">
                {t('embeddings.table.filter_by_status.learned')}：
              </Text>
              <Text size="sm" c="blue">
                {connector.status?.complete || 0}
              </Text>
            </Group>
            <Group
              gap={4}
              className="cursor-pointer"
              onClick={() => {
                onView('process');
              }}
            >
              <Text size="sm" c="gray.6">
                {t('embeddings.table.filter_by_status.learning')}：
              </Text>
              <Text size="sm" c="blue">
                {connector.status?.process || 0}
              </Text>
            </Group>
            <Group
              gap={4}
              className="cursor-pointer"
              onClick={() => {
                onView('ready');
              }}
            >
              <Text size="sm" c="gray.6">
                {t('embeddings.table.filter_by_status.ready')}：
              </Text>
              <Text size="sm" c="blue">
                {connector.status?.ready || 0}
              </Text>
            </Group>
            <Group
              gap={4}
              className="cursor-pointer"
              onClick={() => {
                onView('fail');
              }}
            >
              <Text size="sm" c="gray.6">
                {t('embeddings.table.filter_by_status.failed')}：
              </Text>
              <Text size="sm" c="blue">
                {connector.status?.fail || 0}
              </Text>
            </Group>
            <Group
              gap={4}
              className="cursor-pointer"
              onClick={() => {
                onView('invalid');
              }}
            >
              <Text size="sm" c="gray.6">
                {t('embeddings.table.filter_by_status.unavailable')}：
              </Text>
              <Text size="sm" c="blue">
                {connector.status?.invalid || 0}
              </Text>
            </Group>
          </Stack>
        </Table.Td>
        <Table.Td>
          <Text size="sm">{showRefreshInterval(connector)}</Text>
        </Table.Td>
        <Table.Td>
          <Group gap={5} wrap="nowrap" justify="flex-start">
            <Menu>
              <Menu.Target>
                <ActionIcon variant="subtle">
                  <IconDots size="1rem" />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item leftSection={<IconEye size={14} />} onClick={() => onView()}>
                  {t('dataset.web_connector.view_details')}
                </Menu.Item>
                <Menu.Item leftSection={<IconPencil size={14} />} onClick={handleOpenEdit}>
                  {t('edit')}
                </Menu.Item>
                <Menu.Item leftSection={<IconRotate size={14} />} onClick={handleManualRefresh}>
                  {t('create_v2.integrations.task_management.manual_sync')}
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
            <ActionIcon variant="subtle" color="red" onClick={deleteConnectorConfirm}>
              <IconTrashX size={14} />
            </ActionIcon>
          </Group>
        </Table.Td>
      </Table.Tr>

      {/* V2 编辑连接器 Modal */}
      <Modal opened={opened} onClose={handlers.close} title={t('edit')} centered size="lg">
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Stack gap="lg">
            <div>
              <Text size="sm" fw={500} mb={8}>
                {t('create_v2.integrations.task_management.name')}
              </Text>
              <TextInput
                placeholder={t('create_v2.integrations.task_management.name_placeholder')}
                {...form.getInputProps('name')}
                required
              />
            </div>

            {/* 対象URL */}
            <UrlPatternInput
              label={t('create_v2.website.target_url')}
              value={editUrlPatterns}
              onChange={setEditUrlPatterns}
              placeholder="https://www.yourwebsite.com/FAQ"
              minRows={1}
              variant="include"
            />

            {/* 除外URL */}
            <UrlPatternInput
              label={t('create_v2.website.exclude_url')}
              value={editExcludePatterns}
              onChange={setEditExcludePatterns}
              placeholder="https://example.com/admin/**"
              minRows={1}
              variant="exclude"
            />

            {/* URL プレビュー */}
            <div>
              <Button
                variant="light"
                size="xs"
                onClick={handlePreview}
                loading={previewLoading}
                disabled={editUrlPatterns.filter((u) => u.trim()).length === 0}
              >
                {t('create_v2.website.preview_btn')}
              </Button>
              {previewResult && (
                <div className="mt-2 p-3 bg-gray-50 rounded border" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                  {previewResult.patterns.map((p, i) => (
                    <div key={i} className="mb-2">
                      <Group justify="space-between" mb={2}>
                        <Text size="xs" c="gray.7" className="truncate" style={{ maxWidth: '70%' }}>
                          {p.pattern}
                        </Text>
                        <Text size="xs" fw={500}>
                          {p.matched_count} URLs
                        </Text>
                      </Group>
                      {p.sample_urls?.length > 0 && (
                        <div className="pl-2 border-l-2 border-gray-200">
                          {p.sample_urls.map((url, j) => (
                            <Text key={j} size="xs" c="dimmed" className="truncate" title={url}>
                              {url}
                            </Text>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                  <Divider my="xs" />
                  <Group justify="space-between">
                    <Text size="xs" fw={600}>
                      {t('create_v2.website.preview_total')}
                    </Text>
                    <Text
                      size="xs"
                      fw={600}
                      c={previewResult.total_urls > (Number(editMaxPages) || 400) ? 'red' : undefined}
                    >
                      {previewResult.total_urls} URLs
                      {' / '}
                      {editMaxPages} {t('create_v2.website.preview_page_limit')}
                    </Text>
                  </Group>
                </div>
              )}
            </div>

            {/* ページ数制限 */}
            <Group justify="space-between" wrap="nowrap">
              <Text size="sm" fw={500}>
                {t('create_v2.website.page_limit')}
              </Text>
              <Select
                size="sm"
                value={editMaxPages}
                onChange={(v) => v && setEditMaxPages(v)}
                data={[
                  { value: '400', label: t('create_v2.website.pages_num', { num: 400 }) },
                  { value: '600', label: t('create_v2.website.pages_num', { num: 600 }) },
                  { value: '1200', label: t('create_v2.website.pages_num', { num: 1200 }) },
                  { value: '1800', label: t('create_v2.website.pages_num', { num: 1800 }) },
                  { value: '2000', label: t('create_v2.website.pages_num', { num: 2000 }) },
                  { value: '5000', label: t('create_v2.website.pages_num', { num: 5000 }) },
                ]}
                allowDeselect={false}
                style={{ minWidth: '120px' }}
              />
            </Group>

            {/* 更新頻度 */}
            <ScheduleSelector value={editSchedule} onChange={setEditSchedule} />

            {/* サイトタイプ */}
            <Select
              label={t('create_v2.website.crawler_setting.type_title')}
              description={t('create_v2.website.crawler_setting.type_desc')}
              value={editIsSpa}
              onChange={(v) => v && setEditIsSpa(v)}
              data={[
                { value: '0', label: t('create_v2.website.crawler_setting.type.raw') },
                { value: '1', label: t('create_v2.website.crawler_setting.type.headless') },
              ]}
              allowDeselect={false}
            />

            {/* URL 発見方式 */}
            <Select
              label={t('create_v2.website.crawler_setting.discovery_mode')}
              description={t('create_v2.website.crawler_setting.discovery_mode_desc')}
              value={editDiscoveryMode}
              onChange={(v) => v && setEditDiscoveryMode(v)}
              data={[
                { value: 'auto', label: t('create_v2.website.crawler_setting.discovery.auto') },
                { value: 'sitemap', label: t('create_v2.website.crawler_setting.discovery.sitemap') },
                { value: 'crawler', label: t('create_v2.website.crawler_setting.discovery.crawler') },
              ]}
              allowDeselect={false}
            />

            <Group justify="center" mt="xl">
              <Button type="submit" size="md" px={32} loading={submitting}>
                {t('save')}
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </>
  );
};
